import { z } from "zod";
import { currentYear } from "./currentYear";
import { parseCurrencyToNumber } from "./parseCurrencyToNumber";

const isDumpTruck = (bodyType) =>
  !!bodyType && /dump[_\s-]?truck/i.test(bodyType);

export const HitchType = z.enum([
  "ball_at_bumper",
  "fifth_wheel",
  "tow_boom",
  "ball_in_bed",
  "none",
]);

export const Step6VehicleSchema = z
  .object({
    id: z.string().optional(),

    year: z
      .union([z.string(), z.number()])
      .transform((v) => Number(v))
      .refine((v) => Number.isInteger(v), "Year is required")
      .refine((v) => v >= 1900, "Year must be valid")
      .refine((v) => v <= currentYear + 1, "Year cannot be in the future"),

    make: z.string().min(1, "Make is required"),
    model: z.string().min(1, "Model is required"),

    vin: z
      .string()
      .min(1, "VIN is required")
      .regex(
        /^[A-Za-z0-9]{17}$/,
        "VIN must be exactly 17 characters long and contain only letters and numbers"
      ),

    value: z
      .any()
      .transform(parseCurrencyToNumber)
      .refine((n) => Number.isFinite(n) && n > 0, "Value is required"),

    bodyType: z.string().min(1, "Body Type is required"),

    class: z.preprocess(
      (v) => (typeof v === "number" ? String(v) : v),
      z.string().trim().min(1, "Class is required")
    ),

    includeApdTowing: z.boolean().optional(),
    hitchType: HitchType.optional(),
  });

export const Step6Schema = z
  .object({
    carrier: z
      .array(z.enum(["Cover Whale", "Progressive", "Nirvana", "Accelerant"]))
      .default([]),
    personalUse: z.boolean().default(false),
    vehicleFilesDotCompliant: z.boolean().default(true),

    vehicles: z
      .array(Step6VehicleSchema)
      .min(1, "At least 1 vehicle required"),

    newVehicle: Step6VehicleSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const hasProgressive = data.carrier.includes("Progressive");
    const hasCoverWhale = data.carrier.includes("Cover Whale");
    const hasNirvana = data.carrier.includes("Nirvana");
    const hasCwOrNirvana = hasCoverWhale || hasNirvana;
    const maxAgeFor = (bodyType) => (isDumpTruck(bodyType) ? 26 : 23);

    // Hitch type required if Progressive
    if (hasProgressive) {
      data.vehicles.forEach((v, idx) => {
        if (!v.hitchType) {
          ctx.addIssue({
            code: "custom",
            path: ["vehicles", idx, "hitchType"],
            message: "Hitch Type is required for Progressive.",
          });
        }
      });
    }

    // Age rules: enforced only when quoting Cover Whale without Progressive
    data.vehicles.forEach((v, idx) => {
      const age = currentYear - Number(v.year);
      const maxAge = maxAgeFor(v.bodyType);
      const overLimit = Number.isFinite(age) && age > maxAge;

      if (hasCwOrNirvana && !hasProgressive && overLimit) {
        ctx.addIssue({
          code: "custom",
          path: ["vehicles", idx, "year"],
          message: isDumpTruck(v.bodyType)
            ? "Dump trucks above 26 years of age are not eligible"
            : "Vehicles above 23 years of age are not eligible",
        });
      }
    });

    // Class restriction if Cover Whale
    if (hasCoverWhale) {
      const allowedClasses = ["2a", "2b", "3", "4", "5", "6", "7", "8"];
      data.vehicles.forEach((v, idx) => {
        if (!allowedClasses.includes(v.class.toLowerCase())) {
          ctx.addIssue({
            code: "custom",
            path: ["vehicles", idx, "class"],
            message: `For Cover Whale, class must be one of: ${allowedClasses.join(
              ", "
            )}.`,
          });
        }
      });
    }
  });
